/**
 * TeamSystem Impact - DMA Settings
 * Configuration page for DMA settings: year management, stakeholders, countries, ESG organs, macro-categories
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  ContentCopy as CloneIcon,
  Add as AddIcon,
  Flag as FlagIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useWorkingYear } from '../../../store/workingYearStore';
import { useAuthStore } from '../../../store/authStore';
import { isReadOnlyRole } from '../../../utils/permissions';
import { setupApi, macroCategoryApi } from '../api';
import type { DMASetup, TeamSystemCountry } from '../api/setup.api';
import type { MacroCategory } from '../api/macro-category.api';
import { StakeholderManager } from './StakeholderManager';
import { ESGOrganManager } from './ESGOrganManager';
import { CloneYearDialog } from './CloneYearDialog';
import { YearLifecyclePanel } from './YearLifecyclePanel';

// I18n labels
function useLabels() {
  const { t } = useTranslation();

  return {
    settings: t('dma.dmaSettings.settings'),
    year: t('dma.dmaSettings.year'),
    externalStakeholders: t('dma.dmaSettings.externalStakeholders'),
    countries: t('dma.dmaSettings.countries'),
    internalStakeholders: t('dma.dmaSettings.internalStakeholders'),
    macroCategories: t('dma.dmaSettings.macroCategories'),
    cloneYear: t('dma.dmaSettings.cloneYear'),
    currentYear: t('dma.dmaSettings.currentYear'),
    workingYear: t('dma.dmaSettings.workingYear'),
    startNewYear: t('dma.dmaSettings.startNewYear'),
    newYear: t('dma.dmaSettings.newYear'),
    cloneFromCurrent: t('dma.dmaSettings.cloneFromCurrent'),
    cloneDesc: t('dma.dmaSettings.cloneDesc'),
    create: t('dma.dmaSettings.create'),
    cancel: t('dma.dmaSettings.cancel'),
    activeCountries: t('dma.dmaSettings.activeCountries'),
    selectCountries: t('dma.dmaSettings.selectCountries'),
    save: t('dma.dmaSettings.save'),
    noSetupForYear: t('dma.dmaSettings.noSetupForYear'),
    createSetup: t('dma.dmaSettings.createSetup'),
    yearCreated: t('dma.dmaSettings.yearCreated'),
    countriesSaved: t('dma.dmaSettings.countriesSaved'),
    // Macro-categories labels
    addCategory: t('dma.dmaSettings.addCategory'),
    order: t('dma.dmaSettings.order'),
    code: t('dma.dmaSettings.code'),
    nameIT: t('dma.dmaSettings.nameIT'),
    nameEN: t('dma.dmaSettings.nameEN'),
    actions: t('dma.dmaSettings.actions'),
    editCategory: t('dma.dmaSettings.editCategory'),
    deleteCategory: t('dma.dmaSettings.deleteCategory'),
    confirmDelete: t('dma.dmaSettings.confirmDelete'),
    categoryDeleted: t('dma.dmaSettings.categoryDeleted'),
    categorySaved: t('dma.dmaSettings.categorySaved'),
    noCategories: t('dma.dmaSettings.noCategories'),
    default: t('dma.dmaSettings.default'),
    custom: t('dma.dmaSettings.custom'),
    delete: t('dma.dmaSettings.delete'),
  };
}

// TeamSystem countries
const TEAMSYSTEM_COUNTRIES = [
  { code: 'IT', name: 'Italia', flag: '🇮🇹' },
  { code: 'ES', name: 'España', flag: '🇪🇸' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'CH', name: 'Schweiz', flag: '🇨🇭' },
  { code: 'AL', name: 'Shqipëri', flag: '🇦🇱' },
  { code: 'DK', name: 'Danmark', flag: '🇩🇰' },
  { code: 'TR', name: 'Türkiye', flag: '🇹🇷' },
  { code: 'IL', name: 'Israel', flag: '🇮🇱' },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
      {value === index && children}
    </Box>
  );
}

interface DMASettingsProps {
  assessmentYear: number;
}

export function DMASettings({ assessmentYear }: DMASettingsProps) {
  const labels = useLabels();
  const utente = useAuthStore((s) => s.utente);
  const isReadOnly = isReadOnlyRole(utente?.ruolo);
  const { year: workingYear, setYear: setWorkingYear, availableYears, addYear } = useWorkingYear();
  const [activeTab, setActiveTab] = useState(0);
  const [setup, setSetup] = useState<DMASetup | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // New year dialog
  const [newYearDialogOpen, setNewYearDialogOpen] = useState(false);
  const [newYear, setNewYear] = useState(assessmentYear + 1);
  const [cloneConfig, setCloneConfig] = useState(true);
  const [creatingYear, setCreatingYear] = useState(false);

  // Clone year dialog
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);

  // Countries state
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [savingCountries, setSavingCountries] = useState(false);

  // Macro-categories state
  const [macroCategories, setMacroCategories] = useState<MacroCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<MacroCategory | null>(null);
  const [categoryForm, setCategoryForm] = useState({ code: '', name: '', nameEN: '' });
  const [savingCategory, setSavingCategory] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = useState<string | null>(null);

  useEffect(() => {
    loadSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentYear]);

  useEffect(() => {
    if (activeTab === 4) {
      loadMacroCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, assessmentYear]);

  const loadSetup = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await setupApi.getByYear(assessmentYear);
      setSetup(data);
      setSelectedCountries(data?.activeCountries || []);
    } catch (err: any) {
      setSetup(null);
      setSelectedCountries([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMacroCategories = async () => {
    try {
      setLoadingCategories(true);
      const categories = await macroCategoryApi.list(assessmentYear);
      setMacroCategories(categories);
    } catch (err: any) {
      setMacroCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleCreateNewYear = async () => {
    try {
      setCreatingYear(true);
      setError(null);

      if (cloneConfig && setup?._id) {
        // Clone from current setup
        await setupApi.clone(setup._id, newYear);
      } else {
        // Create empty setup
        await setupApi.create({
          assessmentYear: newYear,
          groupCompanies: [],
          activeCountries: [],
          valueChainConfig: [],
          stakeholderCategories: [],
          naceCodes: [],
          materialityThresholds: { impactScore: 3, financialScore: 3 },
        });
      }

      // Add to available years and switch to new year
      addYear(newYear);
      setWorkingYear(newYear);
      setNewYearDialogOpen(false);
      setSuccess(labels.yearCreated);
      await loadSetup();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreatingYear(false);
    }
  };

  const handleSaveCountries = async () => {
    if (!setup?._id) return;

    try {
      setSavingCountries(true);
      setError(null);
      await setupApi.update(setup._id, { activeCountries: selectedCountries as TeamSystemCountry[] });
      setSuccess(labels.countriesSaved);
      await loadSetup();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingCountries(false);
    }
  };

  const handleCountryToggle = (code: string) => {
    setSelectedCountries(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  // Macro-category handlers
  const handleOpenAddCategory = () => {
    setEditingCategory(null);
    setCategoryForm({ code: '', name: '', nameEN: '' });
    setCategoryDialogOpen(true);
  };

  const handleOpenEditCategory = (category: MacroCategory) => {
    setEditingCategory(category);
    setCategoryForm({ code: category.code, name: category.name, nameEN: category.nameEN });
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    try {
      setSavingCategory(true);
      setError(null);

      if (editingCategory) {
        await macroCategoryApi.update(editingCategory._id, {
          name: categoryForm.name,
          nameEN: categoryForm.nameEN,
        });
      } else {
        await macroCategoryApi.create(assessmentYear, {
          code: categoryForm.code,
          name: categoryForm.name,
          nameEN: categoryForm.nameEN,
        });
      }

      setCategoryDialogOpen(false);
      setSuccess(labels.categorySaved);
      await loadMacroCategories();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deletingCategoryId) return;

    try {
      setError(null);
      await macroCategoryApi.delete(deletingCategoryId);
      setDeleteConfirmOpen(false);
      setDeletingCategoryId(null);
      setSuccess(labels.categoryDeleted);
      await loadMacroCategories();
    } catch (err: any) {
      setDeleteConfirmOpen(false);
      setDeletingCategoryId(null);
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>
        {labels.settings} - {labels.year} {assessmentYear}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab icon={<CalendarIcon />} label={labels.year} iconPosition="start" />
          <Tab label={labels.externalStakeholders} />
          <Tab icon={<FlagIcon />} label={labels.countries} iconPosition="start" />
          <Tab label={labels.internalStakeholders} />
          <Tab icon={<CategoryIcon />} label={labels.macroCategories} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: Year Management */}
      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={3}>
          {/* Current Year Card */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {labels.workingYear}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="h3">{workingYear}</Typography>
                  {setup && <Chip label={setup.status} color={setup.status === 'active' ? 'success' : 'default'} />}
                </Box>
                <FormControl fullWidth size="small">
                  <InputLabel>{labels.year}</InputLabel>
                  <Select
                    value={workingYear}
                    label={labels.year}
                    onChange={(e) => setWorkingYear(e.target.value as number)}
                  >
                    {availableYears.map(y => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>

          {/* Start New Year Card */}
          {!isReadOnly && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {labels.startNewYear}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {labels.cloneFromCurrent}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setNewYearDialogOpen(true)}
                  >
                    {labels.startNewYear}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Clone Year Card */}
          {setup && !isReadOnly && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {labels.cloneYear}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {labels.cloneDesc}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<CloneIcon />}
                    onClick={() => setCloneDialogOpen(true)}
                  >
                    {labels.cloneYear}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* No Setup Warning */}
          {!setup && (
            <Grid item xs={12}>
              <Alert severity="warning">
                {labels.noSetupForYear}
              </Alert>
            </Grid>
          )}

          {/* Archivia / Elimina anni */}
          <Grid item xs={12}>
            <YearLifecyclePanel />
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 1: External Stakeholders */}
      <TabPanel value={activeTab} index={1}>
        <StakeholderManager />
      </TabPanel>

      {/* Tab 2: Countries */}
      <TabPanel value={activeTab} index={2}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {labels.activeCountries}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {labels.selectCountries}
          </Typography>

          <Grid container spacing={2}>
            {TEAMSYSTEM_COUNTRIES.map(country => (
              <Grid item xs={6} sm={4} md={3} key={country.code}>
                <Card
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    borderColor: selectedCountries.includes(country.code) ? 'primary.main' : 'divider',
                    bgcolor: selectedCountries.includes(country.code) ? 'primary.50' : 'background.paper',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleCountryToggle(country.code)}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCountryToggle(country.code);
                    }
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
                    <Checkbox
                      checked={selectedCountries.includes(country.code)}
                      onChange={() => handleCountryToggle(country.code)}
                      size="small"
                    />
                    <Typography variant="h5">{country.flag}</Typography>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{country.code}</Typography>
                      <Typography variant="caption" color="text.secondary">{country.name}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {!isReadOnly && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSaveCountries}
                disabled={savingCountries || !setup}
              >
                {savingCountries ? <CircularProgress size={20} /> : labels.save}
              </Button>
            </Box>
          )}
        </Paper>
      </TabPanel>

      {/* Tab 3: Internal Stakeholders (ESG Organs) */}
      <TabPanel value={activeTab} index={3}>
        <ESGOrganManager />
      </TabPanel>

      {/* Tab 4: Macro-Categories */}
      <TabPanel value={activeTab} index={4}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              {labels.macroCategories} - {assessmentYear}
            </Typography>
            {!isReadOnly && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenAddCategory}
              >
                {labels.addCategory}
              </Button>
            )}
          </Box>

          {loadingCategories ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : macroCategories.length === 0 ? (
            <Alert severity="info">{labels.noCategories}</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={80}>{labels.order}</TableCell>
                    <TableCell width={200}>{labels.code}</TableCell>
                    <TableCell>{labels.nameIT}</TableCell>
                    <TableCell>{labels.nameEN}</TableCell>
                    <TableCell width={100} align="center">{labels.actions}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {macroCategories.map((cat) => (
                    <TableRow key={cat._id} hover>
                      <TableCell>{cat.order}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontFamily="monospace">
                            {cat.code}
                          </Typography>
                          <Chip
                            label={cat.isDefault ? labels.default : labels.custom}
                            size="small"
                            color={cat.isDefault ? 'default' : 'primary'}
                            variant="outlined"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>{cat.name}</TableCell>
                      <TableCell>{cat.nameEN}</TableCell>
                      <TableCell align="center">
                        {!isReadOnly && (
                          <>
                            <Tooltip title={labels.editCategory}>
                              <IconButton
                                size="small"
                                aria-label={labels.editCategory}
                                onClick={() => handleOpenEditCategory(cat)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={labels.deleteCategory}>
                              <IconButton
                                size="small"
                                color="error"
                                aria-label={labels.deleteCategory}
                                onClick={() => {
                                  setDeletingCategoryId(cat._id);
                                  setDeleteConfirmOpen(true);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </TabPanel>

      {/* New Year Dialog */}
      <Dialog open={newYearDialogOpen} onClose={() => setNewYearDialogOpen(false)}>
        <DialogTitle>{labels.startNewYear}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 300 }}>
            <TextField
              label={labels.newYear}
              type="number"
              value={newYear}
              onChange={(e) => setNewYear(parseInt(e.target.value))}
              fullWidth
            />
            {setup && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cloneConfig}
                    onChange={(e) => setCloneConfig(e.target.checked)}
                  />
                }
                label={labels.cloneFromCurrent}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewYearDialogOpen(false)}>{labels.cancel}</Button>
          <Button
            variant="contained"
            onClick={handleCreateNewYear}
            disabled={creatingYear}
          >
            {creatingYear ? <CircularProgress size={20} /> : labels.create}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone Year Dialog */}
      {setup && (
        <CloneYearDialog
          open={cloneDialogOpen}
          onClose={() => setCloneDialogOpen(false)}
          sourceYear={assessmentYear}
          onSuccess={() => {
            setCloneDialogOpen(false);
            setSuccess(labels.yearCreated);
            loadSetup();
          }}
        />
      )}

      {/* Add/Edit Category Dialog */}
      <Dialog
        open={categoryDialogOpen}
        onClose={() => setCategoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingCategory ? labels.editCategory : labels.addCategory}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={labels.code}
              value={categoryForm.code}
              onChange={(e) => setCategoryForm(prev => ({ ...prev, code: e.target.value }))}
              fullWidth
              disabled={!!editingCategory}
              placeholder="es. custom-category"
            />
            <TextField
              label={labels.nameIT}
              value={categoryForm.name}
              onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label={labels.nameEN}
              value={categoryForm.nameEN}
              onChange={(e) => setCategoryForm(prev => ({ ...prev, nameEN: e.target.value }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>{labels.cancel}</Button>
          <Button
            variant="contained"
            onClick={handleSaveCategory}
            disabled={
              savingCategory ||
              !categoryForm.name.trim() ||
              !categoryForm.nameEN.trim() ||
              (!editingCategory && !categoryForm.code.trim())
            }
          >
            {savingCategory ? <CircularProgress size={20} /> : labels.save}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{labels.deleteCategory}</DialogTitle>
        <DialogContent>
          <Typography>{labels.confirmDelete}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{labels.cancel}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteCategory}
          >
            {labels.delete}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
